import { registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptors, withXhr } from '@angular/common/http';
import de from '@angular/common/locales/de';
import en from '@angular/common/locales/en';
import type { ApplicationConfig, Signal } from '@angular/core';
import {
  computed,
  importProvidersFrom,
  inject,
  isDevMode,
  provideAppInitializer,
  provideZonelessChangeDetection,
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import { provideRouter, TitleStrategy, withComponentInputBinding, withPreloading } from '@angular/router';
import { MsalModule } from '@azure/msal-angular';
import { DebugService } from '@core/debug/services/debug.service';
import { flushPendingTabRegistry } from '@core/decorators/register-tab-component.decorator';
import { dxProviders } from '@core/dx/dx.providers';
import { LoggerLevel } from '@core/enums/log-level.enum';
import { MaintenanceService } from '@core/error-pages/maintenance-page/services/maintenance.service';
import { errorInterceptor } from '@core/interceptors/error.interceptor';
import { globalLoadingInterceptor } from '@core/interceptors/global-loading.interceptor';
import { maintenanceInterceptor } from '@core/interceptors/maintenance.interceptor';
import { AmlAdminErrorHandlerService } from '@core/services/aml-admin-error-handler.service';
import { AmlAdminTablePreferenceProvider } from '@core/services/aml-admin-table-preference-provider.service';
import { AuthenticationContextStore } from '@core/services/authentication-context.store';
import { TabComponentRegistryService } from '@core/services/tab-component-registry.service';
import { StorageServiceKeysMap } from '@core/storage/types/storage.types';
import { TabCustomTitleStrategy } from '@core/strategies/tab-custom-title-strategy';
import { environment } from '@env';
import { authenticationInterceptor } from '@features/authentication/interceptors/authentication.interceptor';
import { AuthorizationFacade } from '@features/authorization/facades/authorization.facade';
import type { NamedRole } from '@kerberos-compliance/compliance-shared-ts/core/iam/authorization/enums/named-role';
import type { SentryLibraryConfiguration } from '@kerberos-compliance/compliance-shared-ui';
import {
  auditMetadataInterceptor,
  BaseMaintenanceService,
  ErrorHandlerService,
  formlyProviders,
  LoggerConfigToken,
  ModalService,
  NOTIFICATION_OPTIONS_TOKEN,
  NotificationPositionMap,
  PermissionBasedPreloadingStrategy,
  PRELOAD_USER_ROLES_TOKEN,
  provideDirtyFormUnloadWarning,
  provideErrorHandler,
  provideNavigationRegistry,
  StorageConfigurationToken,
  TablePreferencesProvider,
  USER_PROFILE_STORAGE_KEY_TOKEN,
  UserProfileStore,
  UserTablePreferencesService,
} from '@kerberos-compliance/compliance-shared-ui';
import * as Sentry from '@sentry/angular';
import { extend } from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { NzModalService } from 'ng-zorro-antd/modal';
import { routes } from './app.routes';
import { AmlDeskPlatformUrl, ApiBaseUrl } from './app.tokens';
import { provideMSAL } from './msal-provider';
import { provideBaseFacadeContext } from './providers/base-facade-context.provider';
import { preloadDefaultLanguage, translationProviders } from './translation.providers';

/**
 * Late-bound bridge to the {@link AuthenticationContextStore}. `errorHandlerConfig` is built at module
 * scope — before DI exists — but its `getUserId` callback is only invoked once Sentry reports an error,
 * by which time an app initializer has populated `current`. A holder object is used rather than a
 * reassigned `let` so the binding itself stays immutable.
 */
const authContextStore: { current: AuthenticationContextStore | undefined } = { current: undefined };

const errorHandlerConfig: SentryLibraryConfiguration = {
  dsn: environment.sentryUrl,
  // Sentry is off in local dev by default; the `sentry` debug flag force-enables delivery for testing.
  // Gate on productionBuild (not isDevMode()): deployed builds like preview run with optimization
  // disabled, so isDevMode() is true there and would wrongly suppress delivery.
  enableSentry: environment.productionBuild || (environment.debugFlags ?? []).includes('sentry'),
  integrations: [Sentry.browserTracingIntegration()],
  tracesSampleRate: 1,
  release: environment.version,
  environment: environment.target,
  enableConsoleLogging: isDevMode(),
  getUserId: () => authContextStore.current?.getAuthenticatedUser()?.sub,
};

// eslint-disable-next-line unicorn/no-top-level-side-effects
registerLocaleData(en);
// eslint-disable-next-line unicorn/no-top-level-side-effects
registerLocaleData(de);
// dayjs plugins are registered globally, once, before any component or pipe calls .utc() or .tz()
// eslint-disable-next-line unicorn/no-top-level-side-effects
extend(utc);
// eslint-disable-next-line unicorn/no-top-level-side-effects
extend(timezone);

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    provideRouter(routes, withComponentInputBinding(), withPreloading(PermissionBasedPreloadingStrategy)),
    provideNavigationRegistry(),
    // Inert unless the activated route opts in with `data: { warnOnUnload: true }`.
    provideDirtyFormUnloadWarning(),
    {
      provide: PRELOAD_USER_ROLES_TOKEN,
      useFactory: (): Signal<NamedRole[]> => {
        const facade = inject(AuthorizationFacade);
        return computed(() => facade.getGrants().map((g) => g.roleDisplayName));
      },
    },
    provideAppInitializer(preloadDefaultLanguage()),
    provideAppInitializer(() => {
      const service = inject(TabComponentRegistryService);
      flushPendingTabRegistry(service);
    }),
    provideAppInitializer(() => {
      authContextStore.current = inject(AuthenticationContextStore);
    }),
    provideErrorHandler(errorHandlerConfig),
    {
      provide: DebugService,
      useFactory: (): DebugService => {
        const service = new DebugService();
        service.setDebugEnabled(isDevMode());
        return service;
      },
    },
    {
      provide: StorageConfigurationToken,
      useValue: {
        prefix: 'aad_',
        storage: localStorage,
        keys: Object.values(StorageServiceKeysMap),
      },
    },
    {
      provide: LoggerConfigToken,
      useValue: {
        minLevel: environment.productionBuild ? LoggerLevel.Warn : LoggerLevel.Info,
        enableConsole: !environment.productionBuild,
      },
    },
    ...translationProviders,
    ...dxProviders(),
    provideBaseFacadeContext(),
    // Order matters! Authentication interceptor should run before error interceptor
    provideHttpClient(
      withXhr(),
      withInterceptors([
        authenticationInterceptor,
        maintenanceInterceptor,
        globalLoadingInterceptor,
        auditMetadataInterceptor,
        errorInterceptor,
      ]),
    ),
    { provide: ApiBaseUrl, useValue: environment.amlBaseApiUrl },
    ...(environment.enableMsal ? [importProvidersFrom(MsalModule), ...provideMSAL()] : []),
    { provide: AmlDeskPlatformUrl, useValue: environment.amlDeskPlatformUrl },
    // NzModalService is a bare @Injectable() and NzModalModule provides nothing, so it has to be
    // provided explicitly. It belongs here rather than on a component: the root-scoped ModalService
    // depends on it, so anything resolved from the environment injector — route guards,
    // AbstractNavigationService, KycModalService — needs it in this injector.
    NzModalService,
    ModalService,
    Title,
    {
      provide: TitleStrategy,
      useClass: TabCustomTitleStrategy,
    },
    formlyProviders,
    {
      provide: NOTIFICATION_OPTIONS_TOKEN,
      useValue: {
        placement: NotificationPositionMap.topRight,
      },
    },
    { provide: BaseMaintenanceService, useExisting: MaintenanceService },
    {
      provide: ErrorHandlerService,
      useExisting: AmlAdminErrorHandlerService,
    },
    {
      provide: USER_PROFILE_STORAGE_KEY_TOKEN,
      useValue: StorageServiceKeysMap.UserProfile,
    },
    UserProfileStore,
    UserTablePreferencesService,
    { provide: TablePreferencesProvider, useClass: AmlAdminTablePreferenceProvider },
  ],
};
